import { useLazyQuery, useQuery } from '@apollo/client';
import classNames from 'classnames';
import debounce from 'debounce';
import { observer } from 'mobx-react-lite';
import moment from 'moment';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Icon, IconClose, IconMagnifier } from '@flixbus/honeycomb-icons-react';
import {
	Box,
	Button,
	Divider,
	Heading,
	Input,
	Panel,
	PanelContent,
	PanelHeader,
	Popup,
	PopupSection,
	Spinner,
	Text,
} from '@flixbus/honeycomb-react';

import { SIDEBAR_NAMES } from 'common/activeElements/sidebars/sidebarsNames';
import { formatOverlappingActivities } from 'common/OverlappingActivitiesBanner/helpers/overlappingActivities';
import OverlappingBanner from 'common/OverlappingActivitiesBanner/OverlappingBanner';
import { WA_OVERLAPPING_ACTIVITIES_RESPONSE } from 'constants/activityTypes';
import { client } from 'contexts/ApolloClient/apolloClient';
import { useActiveElements } from 'global/ActiveElements/useActiveElements';
import { standardError, standardSuccess } from 'global/commonMessages';
import { notificationStore } from 'global/NotificationStore';
import { ASSIGNMENT_STATUSES } from 'pages/RideDetails/components/Assigment/const';
import DriverCard from 'pages/RideDetails/components/DriverCard';
import { CREATE_WORK_ASSIGMENT } from 'pages/RideDetails/gql/createWorkAssignment';
import { VALIDATE_AND_OVERLAPS_BY_SEQUENCE_GROUP } from 'pages/WorkAssignments/gql/eligibilityValidation';
import { FETCH_ELIGIBLE_BY_SEQUENCE_GROUP } from 'pages/WorkAssignments/gql/fetchEligibleDrivers';
import { SEARCH_CANDIDATES_BY_SEQUENCE_GROUP } from 'pages/WorkAssignments/gql/searchCandidatesBySequenceGroup';
import { FETCH_ASSIGNMENTS_WITH_FILTERS } from 'pages/WorkAssignments/gql/workAssignmentSearch';
import { createAssignmentErrorNotification } from 'utils/assignmentCustomErrorGenerator';
import { sortCrewMembersByIdleTime } from 'utils/driverIdleSorter';

import { assignJobReturnSidebarStore } from './AssignJobReturnSidebarStore';
import ReturnJobSection from './ReturnJobSection';
import { assignJobReturnSidebarText } from './text';

import styles from './styles.module.css';
import rideStyles from 'pages/RideDetails/components/DriversWidget/styles.module.css';

const mapEligibleToDriver = (eligible, index) => ({
	id: eligible?.crewMember?.id ?? `driver-${index}`,
	crewMember: eligible?.crewMember ?? {},
	availabilityInfo: eligible?.availabilityInfo ?? {},
});

const AssignJobReturnSidebar = observer(
	({ active = true, onClose = () => {}, selection = null }) => {
		const [searchText, setSearchText] = useState('');
		const [isSearchMode, setIsSearchMode] = useState(false);
		const [searchedDrivers, setSearchedDrivers] = useState([]);
		const [isSearchLoading, setIsSearchLoading] = useState(false);
		const activeElements = useActiveElements();
		const isReturnSidebarOpen = activeElements.active?.name === SIDEBAR_NAMES.returnJobDetails;
		const isAssignOnly = selection?.mode === 'assignOnly';
		const hasOverlaps = assignJobReturnSidebarStore.overlaps.length > 0;
		const hasEligibilityReasons = assignJobReturnSidebarStore.reasons.length > 0;

		const [searchCandidates] = useLazyQuery(SEARCH_CANDIDATES_BY_SEQUENCE_GROUP, {
			fetchPolicy: 'no-cache',
			notifyOnNetworkStatusChange: true,
			onCompleted: (data) => {
				const results = data?.searchWorkAssignmentCandidates ?? [];
				const sorted = sortCrewMembersByIdleTime(results);
				setSearchedDrivers(
					sorted
						.filter((item) => item?.crewMember?.id)
						.map((item, index) => ({
							...mapEligibleToDriver(item, index),
							assignmentKey: `${item?.crewMember?.id ?? 'no-crew'}__search_${index}`,
						}))
				);
				setIsSearchLoading(false);
			},
			onError: () => {
				setSearchedDrivers([]);
				setIsSearchLoading(false);
			},
		});

		const debounceRef = useRef(null);

		useEffect(() => {
			debounceRef.current = debounce((variables) => {
				searchCandidates({ variables });
			}, 300);

			return () => {
				if (debounceRef.current) {
					debounceRef.current.clear();
				}
			};
		}, [searchCandidates]);

		const handleClose = () => {
			assignJobReturnSidebarStore.reset();
			if (isReturnSidebarOpen) {
				activeElements.closeAll();
			}
			onClose();
		};

		const getAssignmentContext = (driver) => {
			const selectedReturnJob = assignJobReturnSidebarStore.isSelectedDriver(driver?.assignmentKey)
				? assignJobReturnSidebarStore.selectedReturnJob
				: null;

			return selectedReturnJob ?? selection;
		};

		const callAssignMutation = async ({
			context,
			crewMemberId,
			overlappingActivityAction,
			delayMinutes = null,
		}) => {
			const response = await client.mutate({
				mutation: CREATE_WORK_ASSIGMENT,
				variables: {
					newAssignment: {
						sequenceGroupId: context?.sequenceGroupId,
						fromStationId: context?.fromStationId,
						toStationId: context?.toStationId,
						crewMemberId,
						activityType: context?.activityType,
					},
					overlappingActivityAction,
					delayMinutes,
				},
				fetchPolicy: 'no-cache',
			});

			return response?.data?.batchAssignCrewMemberV2 ?? null;
		};

		const handleAssignPayload = (payload, context, driver) => {
			if (!payload) {
				notificationStore.setNotification(standardError);
				return false;
			}

			if (Object.hasOwn(payload, 'errors')) {
				const errorList =
					payload?.errors?.flatMap((errorItem) => errorItem?.errors ?? []).filter(Boolean) ?? [];
				notificationStore.setNotification(
					createAssignmentErrorNotification(
						context?.fromStationName ?? 'N/A',
						context?.toStationName ?? 'N/A',
						errorList
					)
				);
				return false;
			}

			if (payload.__typename === WA_OVERLAPPING_ACTIVITIES_RESPONSE) {
				assignJobReturnSidebarStore.showValidationIssues({
					driver,
					reasons: assignJobReturnSidebarStore.reasons,
					overlaps: payload?.overlappingActivities ?? [],
					minDelayMinutes: payload?.minDelayMinutes ?? null,
					maxDelayMinutes: payload?.maxDelayMinutes ?? null,
					cannotCancelOverlaps: (payload?.overlappingActivities ?? []).some(
						(overlappingActivity) => !overlappingActivity.isCancellable
					),
				});
				return false;
			}

			return true;
		};

		const executeAssignment = async (driver, overlappingActivityAction = null) => {
			const driverId = driver?.crewMember?.id;
			const delayMinutes = assignJobReturnSidebarStore.allowToDelayOverlaps
				? assignJobReturnSidebarStore.delayMinutes
				: null;

			const primaryPayload = await callAssignMutation({
				context: selection,
				crewMemberId: driverId,
				overlappingActivityAction,
				delayMinutes,
			});
			if (!handleAssignPayload(primaryPayload, selection, driver)) {
				return false;
			}

			const selectedReturnJob = assignJobReturnSidebarStore.selectedReturnJob;
			if (selectedReturnJob) {
				const returnPayload = await callAssignMutation({
					context: selectedReturnJob,
					crewMemberId: driverId,
					overlappingActivityAction,
					delayMinutes,
				});
				if (!handleAssignPayload(returnPayload, selectedReturnJob, driver)) {
					return false;
				}
			}

			notificationStore.setNotification(standardSuccess);
			assignJobReturnSidebarStore.reset();
			activeElements.closeAll();
			selection?.onAssignSuccess?.();
			return true;
		};

		const handleAssign = async (driver) => {
			const assignmentContext = getAssignmentContext(driver);

			if (
				!assignmentContext?.sequenceGroupId ||
				!assignmentContext?.fromStationId ||
				!assignmentContext?.toStationId ||
				!assignmentContext?.activityType ||
				!driver?.crewMember?.id
			) {
				notificationStore.setNotification(standardError);
				return;
			}

			assignJobReturnSidebarStore.startAssigning(driver);

			try {
				const { data: validationData } = await client.query({
					query: VALIDATE_AND_OVERLAPS_BY_SEQUENCE_GROUP,
					fetchPolicy: 'no-cache',
					variables: {
						crewMemberId: driver?.crewMember?.id,
						workAssignmentSequenceGroupId: assignmentContext?.sequenceGroupId,
						fromStationId: assignmentContext?.fromStationId,
						toStationId: assignmentContext?.toStationId,
						activityType: assignmentContext?.activityType,
					},
				});

				const eligibility = validationData?.eligibility ?? [];
				const overlapsResponse = validationData?.overlaps ?? {};
				const overlaps = overlapsResponse.overlappingActivities ?? [];

				if (eligibility.length > 0 || overlaps.length > 0) {
					assignJobReturnSidebarStore.showValidationIssues({
						driver,
						reasons: eligibility,
						overlaps,
						minDelayMinutes: overlapsResponse.minDelayMinutes ?? null,
						maxDelayMinutes: overlapsResponse.maxDelayMinutes ?? null,
					});
					return;
				}

				await executeAssignment(driver, null);
			} catch (error) {
				notificationStore.setNotification(standardError);
			} finally {
				assignJobReturnSidebarStore.stopAssigning();
			}
		};

		const handleAssignAnyway = async () => {
			const driver = assignJobReturnSidebarStore.selectedDriver;
			if (!driver?.crewMember?.id) {
				return;
			}

			const overlappingActivityAction = assignJobReturnSidebarStore.allowToDeleteOverlaps
				? 'cancel'
				: assignJobReturnSidebarStore.allowToDelayOverlaps
					? 'delay'
					: null;

			assignJobReturnSidebarStore.startAssigning(driver);

			try {
				await executeAssignment(driver, overlappingActivityAction);
			} catch (error) {
				notificationStore.setNotification(standardError);
			} finally {
				assignJobReturnSidebarStore.stopAssigning();
			}
		};

		useEffect(() => {
			if (!isReturnSidebarOpen && assignJobReturnSidebarStore.selectedAssignmentId !== null) {
				assignJobReturnSidebarStore.closeReturnJobSidebar();
			}
		}, [isReturnSidebarOpen]);

		useEffect(() => {
			assignJobReturnSidebarStore.reset();
		}, [selection?.sequenceGroupId]);

		const variables = useMemo(() => {
			if (
				!selection?.sequenceGroupId ||
				!selection?.fromStationId ||
				!selection?.toStationId ||
				!selection?.activityType
			) {
				return null;
			}

			return {
				workAssignmentSequenceGroupId: selection.sequenceGroupId,
				fromStationId: selection.fromStationId,
				toStationId: selection.toStationId,
				activityType: selection.activityType,
			};
		}, [selection]);

		const { data, loading, error } = useQuery(FETCH_ELIGIBLE_BY_SEQUENCE_GROUP, {
			skip: !active || !variables,
			variables,
			fetchPolicy: 'no-cache',
		});

		const driverItems = useMemo(() => {
			const results = data?.eligibleCrewMembersV2 ?? [];
			const sorted = sortCrewMembersByIdleTime(results);
			return sorted
				.filter((item) => item?.crewMember?.id)
				.map((item, index) => ({
					...mapEligibleToDriver(item, index),
					assignmentKey: `${item?.crewMember?.id ?? 'no-crew'}__${index}`,
				}));
		}, [data]);

		const displayedDrivers = isSearchMode ? searchedDrivers : driverItems;

		useEffect(() => {
			if (!variables || displayedDrivers.length === 0) return;

			assignJobReturnSidebarStore.validateDrivers(displayedDrivers, variables);

			return () => {
				assignJobReturnSidebarStore.cancelBulkValidation();
			};
		}, [displayedDrivers, variables]);

		const handleSearchChange = (e) => {
			const value = e.target.value;
			setSearchText(value);

			if (value === '') {
				setSearchedDrivers([]);
				setIsSearchLoading(false);
				return;
			}

			setIsSearchLoading(true);
			if (debounceRef.current) {
				debounceRef.current({
					searchFilter: { text: value },
					workAssignmentSequenceGroupId: selection?.sequenceGroupId,
					fromStationId: selection?.fromStationId,
					toStationId: selection?.toStationId,
					activityType: selection?.activityType,
				});
			}
		};

		const handleSearchClose = () => {
			setSearchText('');
			setIsSearchMode(false);
			setSearchedDrivers([]);
			setIsSearchLoading(false);
		};

		const openReturnJobSidebar = async (driver, { showSelectedReturnJob = false } = {}) => {
			if (isReturnSidebarOpen) {
				return;
			}

			const sidebarProps = {
				selectedAssignmentKey: driver?.assignmentKey ?? null,
				primarySequenceGroupId: selection?.sequenceGroupId ?? null,
			};

			assignJobReturnSidebarStore.openReturnJobSidebar(driver?.assignmentKey ?? null, {
				showSelectedReturnJob,
			});
			activeElements.pushAndOpen(SIDEBAR_NAMES.returnJobDetails, {
				...sidebarProps,
				assignments: [],
				loading: true,
			});

			let assignments = [];
			if (selection?.runName && selection?.plannedArrival) {
				try {
					const { data: assignmentsData } = await client.query({
						query: FETCH_ASSIGNMENTS_WITH_FILTERS,
						fetchPolicy: 'no-cache',
						variables: {
							pagination: { page: 0, size: 20 },
							filter: {
								runNames: [selection.runName],
								fromDate: moment(selection.plannedArrival).format('YYYY-MM-DD'),
								toDate: moment(selection.plannedArrival).add(2, 'days').format('YYYY-MM-DD'),
								reconciliationStatus: ASSIGNMENT_STATUSES.PENDING,
							},
						},
					});
					const allAssignments = assignmentsData?.workAssignmentSearchV2?.results ?? [];
					const selectedJobCode = selection?.job?.code;
					assignments = selectedJobCode
						? allAssignments.filter((item) => item?.job?.code !== selectedJobCode)
						: allAssignments;
				} catch (e) {
					// TODO: handle empty assignments
				}
			}

			activeElements.setActiveElement(SIDEBAR_NAMES.returnJobDetails, {
				...sidebarProps,
				assignments,
				loading: false,
			});
		};

		const searchNode = (
			<Input
				id="assign-job-return-filter"
				label={assignJobReturnSidebarText.searchLabel}
				placeholder={assignJobReturnSidebarText.searchPlaceholder}
				onFocus={() => setIsSearchMode(true)}
				onChange={handleSearchChange}
				value={searchText}
				iconRight={
					isSearchMode ? (
						<Button onClick={handleSearchClose}>
							<Icon id="alignIcon" InlineIcon={IconClose} />
						</Button>
					) : (
						<Icon id="alignIcon" InlineIcon={IconMagnifier} />
					)
				}
			/>
		);

		if (loading) {
			return (
				<Panel
					id="assign-job-return-sidebar"
					active={active}
					hasOverlay
					onOverlayClick={handleClose}
				>
					<PanelHeader closeButtonProps={{ label: 'Close', onClick: handleClose }}>
						{assignJobReturnSidebarText.panelHeader}
					</PanelHeader>
					<PanelContent extraClasses={`${rideStyles.driverswidget} ${rideStyles.loading}`}>
						<Spinner />
					</PanelContent>
				</Panel>
			);
		}

		if (error) {
			return (
				<Panel
					id="assign-job-return-sidebar"
					active={active}
					hasOverlay
					onOverlayClick={handleClose}
				>
					<PanelHeader closeButtonProps={{ label: 'Close', onClick: handleClose }}>
						{assignJobReturnSidebarText.panelHeader}
					</PanelHeader>
					<PanelContent>{assignJobReturnSidebarText.errorMessage}</PanelContent>
				</Panel>
			);
		}

		return (
			<>
				<Panel
					id="assign-job-return-sidebar"
					active={active}
					hasOverlay
					onOverlayClick={handleClose}
					extraClasses={rideStyles.width}
					position="default"
				>
					<PanelHeader closeButtonProps={{ label: 'Close', onClick: handleClose }}>
						{assignJobReturnSidebarText.panelHeader}
					</PanelHeader>
					<PanelContent extraClasses={rideStyles.width}>
						{searchNode}

						<div className={rideStyles.scroll}>
							{isSearchMode ? (
								<>
									<Box extraClasses={rideStyles.info}>
										<Icon InlineIcon={IconMagnifier} extraClasses={rideStyles.icon} size={5} />
										{assignJobReturnSidebarText.searchInfo}
									</Box>
									{isSearchLoading && (
										<div className={styles.searchLoading}>
											<Spinner />
										</div>
									)}
								</>
							) : (
								<h3 className={styles.perfectFit}>{assignJobReturnSidebarText.perfectFit}</h3>
							)}

							{displayedDrivers.length > 0 ? (
								<>
									{displayedDrivers.map((driver, index) => {
										const driverId = driver?.crewMember?.id;
										const isDriverBeingAssigned =
											assignJobReturnSidebarStore.assigningDriverId != null &&
											assignJobReturnSidebarStore.assigningDriverId === driverId;
										const isSelectedDriver = assignJobReturnSidebarStore.isSelectedDriver(
											driver?.assignmentKey
										);
										const eligibilityIssues =
											assignJobReturnSidebarStore.bulkValidationMap[driverId];

										return (
											<div
												className={classNames(rideStyles.item, {
													[styles.disabledItem]:
														isReturnSidebarOpen &&
														assignJobReturnSidebarStore.selectedAssignmentId !== null &&
														assignJobReturnSidebarStore.selectedAssignmentId !==
															driver?.assignmentKey,
												})}
												key={driver?.assignmentKey ?? `driver-${index}`}
											>
												<DriverCard
													onAssign={() => handleAssign(driver)}
													driver={driver}
													isInAssignState={isDriverBeingAssigned}
													elegibilityIssues={eligibilityIssues}
													isAssignDisabled={
														assignJobReturnSidebarStore.assigningDriverId != null ||
														(!isAssignOnly && !isSelectedDriver) ||
														isReturnSidebarOpen
													}
													forceBottomVisible={
														isReturnSidebarOpen &&
														assignJobReturnSidebarStore.selectedAssignmentId ===
															driver?.assignmentKey
													}
													{...(!isAssignOnly && {
														bottomSlotVariant: isSelectedDriver ? 'selected' : 'action',
														bottomSlot: (
															<ReturnJobSection
																selectedReturnJob={
																	isSelectedDriver
																		? assignJobReturnSidebarStore.selectedReturnJob
																		: null
																}
																extraActionLabel={assignJobReturnSidebarText.returnJobAction}
																driverFullName={driver?.crewMember?.fullName}
																onSelectedReturnJobClick={() =>
																	openReturnJobSidebar(driver, {
																		showSelectedReturnJob: true,
																	})
																}
																onClearSelectedReturnJob={() => {
																	assignJobReturnSidebarStore.clearSelectedReturnJob();
																}}
																onExtraAction={() => openReturnJobSidebar(driver)}
															/>
														),
													})}
												/>
											</div>
										);
									})}
								</>
							) : (
								<>
									{!isSearchLoading && (
										<div className={rideStyles.bold}>
											{isSearchMode
												? assignJobReturnSidebarText.noCrewMembersFound
												: assignJobReturnSidebarText.noEligibleCrewMembers}
										</div>
									)}
								</>
							)}
						</div>
					</PanelContent>
				</Panel>
				<Popup
					extraClasses={rideStyles.popup}
					active={assignJobReturnSidebarStore.showsReasons}
					hasOverlay
				>
					<PopupSection type="title">
						<Heading
							id="assign-job-return-warning-title"
							size={2}
							sectionHeader
							flushSpace
							extraClasses={rideStyles.warningTitle}
						>
							{assignJobReturnSidebarText.warningTitle}
						</Heading>
						<Text Elem="strong" extraClasses={rideStyles.bold}>
							{`${assignJobReturnSidebarStore.selectedDriver?.crewMember?.fullName ?? ''}: ${selection?.fromStationName ?? ''} → ${selection?.toStationName ?? ''}`}
						</Text>
					</PopupSection>
					<PopupSection type="content">
						<Text extraClasses={rideStyles.leftText}>
							{assignJobReturnSidebarText.warningDescription}
						</Text>
						{hasEligibilityReasons && (
							<ul className={rideStyles.reasonsList}>
								{assignJobReturnSidebarStore.reasons.map((reason, index) => (
									<li
										className={rideStyles.reasonItem}
										key={`${reason?.failedRule ?? 'reason'}-${index}`}
									>
										<Text Elem="strong" extraClasses={rideStyles.bold}>
											{reason?.defaultMessage}
										</Text>
									</li>
								))}
							</ul>
						)}
						<Text extraClasses={rideStyles.leftText}>
							{assignJobReturnSidebarText.warningDisclaimer}
						</Text>
						{hasOverlaps && (
							<div className={rideStyles.driversOverlappingBanner}>
								<Divider extraClasses={rideStyles.driversDivider} />
								<OverlappingBanner
									overlappingList={formatOverlappingActivities(
										assignJobReturnSidebarStore.overlaps,
										null
									)}
									overlappingAction={assignJobReturnSidebarStore.overlappingAction}
									onOverlappingActionChange={(value) =>
										assignJobReturnSidebarStore.setOverlappingAction(value)
									}
									cannotCancelOverlaps={assignJobReturnSidebarStore.cannotCancelOverlaps}
									isDelayOptionAvailable={assignJobReturnSidebarStore.isDelayOptionAvailable}
									delayMinutes={assignJobReturnSidebarStore.delayMinutes}
									onDelayMinutesChange={(value) =>
										assignJobReturnSidebarStore.setDelayMinutes(value)
									}
									delayError={assignJobReturnSidebarStore.delayError}
									minDelayMinutes={assignJobReturnSidebarStore.minDelayMinutes}
								/>
							</div>
						)}
					</PopupSection>
					<PopupSection type="actions">
						<Button onClick={() => assignJobReturnSidebarStore.hideReasons()}>
							{assignJobReturnSidebarText.cancelButton}
						</Button>
						<Button
							disabled={assignJobReturnSidebarStore.isAssignmentDisabled}
							loading={assignJobReturnSidebarStore.assigningDriverId != null}
							appearance="danger"
							onClick={() => handleAssignAnyway()}
						>
							{assignJobReturnSidebarText.assignAnywayButton}
						</Button>
					</PopupSection>
				</Popup>
			</>
		);
	}
);

export default AssignJobReturnSidebar;
